import React, { useEffect, useState } from 'react';
import { forEach, transform, isEmpty, findIndex } from 'lodash';
import isNumber from 'lodash/isNumber';
import { translate, getTranslatedKPINameV2 } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';

import { afterChartReDrawStringData, chartLegendItemClick } from '../../../../helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { TrendTooltip } from 'sharedComponents/ChartComponents/Trend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold } from 'helpers/constants';
import { setMaxPrecision } from 'helpers/formatters';
import { RemovableLegend } from 'sharedComponents/ChartComponents/CommonLegend';
import { LEVEL, LIMIT_DIRECTION, LIMIT_LEVELS, LIMIT_NAME } from '../../../LimitConfigurationBeta/components/constants';
import { COMMENT_YAXIS_ID, LIMIT_LINE_MARKERS, LIMIT_LINES_ORDER } from './constants';
import { getMergedTrendKpiList } from '../../../commonTrendHelpers';
import { multiChartOptions } from '../../constants';
import { IconTrendComment } from 'svgIcons/MotionPortalIcons';
import TrendCommentsTooltip from '../../SingleChartView/Trend/TrendComments/TrendCommentsTooltip';
import TrendGroupCommentsTooltip from '../../SingleChartView/Trend/TrendComments/TrendGroupCommentsTooltip';
import { energyConsumptionSignals, energyViewModes, STACKED_AREA_CHART } from '../../SingleChartView/Trend/EnergyRelatedSignalsList/constants';
import useUserCapability from 'sharedHooks/useUserCapability';
import { getXAxisLabelDateFormat } from '../../../helpersV2';

const getChartConfig = removeKPIData => {
    return {
        title: {
            text: null
        },
        chart: {
            zoomType: 'x',
            height: null,
            events: {
                render: function (chartInstance) {
                    afterChartReDrawStringData(chartInstance.target.container.id, removeKPIData);

                    // Add custom comment icons from dataLabels
                    const chart = this;
                    const series = chart?.series;
                    if (series?.length) {
                        if (chart?.customComments?.length) {
                            chart?.customComments?.forEach((comment) => {
                                comment?.destroy();
                            });
                        }
                        const scatterSeries = series.find((s) => s.options.id === 'comment-series');
                        chart.customComments = [];
                        scatterSeries?.options?.data?.forEach((point) => {
                            const formatter = point?.dataLabels?.formatter.call({ point });
                            const svgWidth = 16, svgHeight = 16;

                            // Adjust icon position if timestamp is near edges to prevent cropping
                            const xAxis = chart.xAxis[0];
                            const xPixelPosition = xAxis.toPixels(point.x);
                            const xMin = xAxis.toPixels(xAxis.min);
                            const xMax = xAxis.toPixels(xAxis.max);
                            const distanceFromMax = xMax - xPixelPosition;
                            const distanceFromMin = xPixelPosition - xMin;

                            let iconOffset;
                            if (distanceFromMax < svgWidth * 2) {
                                iconOffset = svgWidth * 1.5;
                            } else if (distanceFromMin < svgWidth) {
                                iconOffset = 0;
                            } else {
                                iconOffset = svgWidth / 2;
                            }

                            const commentYAxis = chart.yAxis.find(axis => axis.options.id === COMMENT_YAXIS_ID);
                            if (!commentYAxis) {
                                return;
                            }
                            const label = chart?.renderer?.label(
                                formatter,
                                xPixelPosition - iconOffset,
                                commentYAxis.toPixels(point.y) - svgHeight / 2,
                                null,
                                null,
                                null,
                                true
                            ).attr({
                                class: 'highcharts-comment-icon',
                                zIndex: 10
                            }).add();

                            chart.customComments.push(label);
                        });
                    }
                }
            },
            marginBottom: 115
        },
        boost: {
            allowForce: false //Workaround for highchart bug. Allows correctly disabling boost, when zooming in.
        },
        time: {
            useUTC: false
        },
        xAxis: {
            type: 'datetime',
            id: 'x',
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
            },
            labels: {
                formatter: function () {
                    return getXAxisLabelDateFormat(this.value);
                }
            },
            crosshair: {
                enabled: true,
                width: 2,
                color: colors.tundora,
            },
            minPadding: 0,
            maxPadding: 0,
            lineColor: 'rgba(31, 31, 31, 1)',
            lineWidth: 2
        },
        legend: {
            useHTML: true,
            layout: 'horizontal',
            align: 'right',
            enabled: true,
            reversed: false,
            itemWidth: 300,
            itemDistance: 16,
            itemMarginTop: 10,
            maxHeight: 85,
            itemStyle: {
                fontSize: '14px',
                fontFamily: 'ABBvoice-Regular',
                letterSpacing: '0',
                lineHeight: '17px',
                textAlign: 'center',
                textOverflow: 'ellipsis',
            },
            symbolRadius: 0,
            squareSymbol: false,
            symbolHeight: 0,
            symbolWidth: 0,
            y: 4
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            valueDecimals: 2,
            useHTML: true,
            backgroundColor: null,
            borderWidth: 0,
            dateTimeLabelFormats: {
                day: '%d.%m.%Y<br/>%H:%M',
                hour: '%d.%m %H:%M',
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 1,
                animation: true,
                boostThreshold: 1,
                stickyTracking: false
            },
            line: {
                boostThreshold: 1,
            },
        },
        lang: {
            noData: 'No data'
        },
        noData: {
            useHTML: true
        },
        exporting: {
            enabled: false,
            buttons: {
                contextButton: {
                    menuItems: ['downloadPNG'],
                    verticalAlign: 'bottom',
                    x: 10,
                    y: 0
                }
            }
        }
    };
};

const getMeasurements = (config, measurements, showRangeData) => {
    const averages = [];
    const rangedata = [];
    const applyOffset = (val) => isNumber(val) && config?.offset
        ? setMaxPrecision(val + +config.offset, config?.precision) : val;

    if (measurements?.length > 0) {
        forEach(measurements, (measurement) => {
            if (measurement?.timestamp !== null) {
                const date = new Date(measurement?.timestamp).getTime();
                averages.push([date, applyOffset(measurement?.value)]);
                if (showRangeData) {
                    rangedata.push([date,
                        isNumber(measurement?.min) ? applyOffset(measurement.min) : null,
                        isNumber(measurement?.max) ? applyOffset(measurement.max) : null]);
                }
            }
        });
    }

    return { averages, rangedata };
};

const generateMarkerData = (averages, limitValue) => {
    if (averages?.length) {
        return averages.map((average) => {
            return [average[0], limitValue];
        });
    }
};

// Get selected series index from trendKPIsList and then assigned it as an id to series for maintain unique ID.
const getSeriesIndexFromList = (kpiConfig, trendKPIsList) => {
    return findIndex(trendKPIsList, (kpi) => kpi.timeseriesKey === kpiConfig.measurementTypeIdentifier);
};

const addLimitsInSeries = (limit, limtsDataSeries, unit = '', yAxisId) => {
    const direction = limit?.direction === LEVEL.UpperLimit || limit?.direction === LEVEL.Upper ? LIMIT_DIRECTION.Upper : LIMIT_DIRECTION.Lower;
    const selectedLimitName = translate(LIMIT_NAME[`${direction}${limit?.level}`]);
    return {
        data: limtsDataSeries,
        name: selectedLimitName,
        showInLegend: false,
        color: limit?.level === LIMIT_LEVELS.Alarm ? colors.pomegranate : colors.orangePeel,
        id: `${limit?.level}_${limit?.direction}_${crypto.randomUUID()}`,
        lineWidth: 1,
        dashStyle: 'Solid',
        unit: unit,
        yAxis: yAxisId,
        marker: {
            enabled: false,
            symbol: LIMIT_LINE_MARKERS[`${direction}${limit?.level}`]
        },
        tooltip: {
            valueSuffix: ` ${unit}`,
            isUserLimit: limit?.isUserLimit,
        },
        limitValue: limit?.limitValue
    };
};

// Helper function to group comments that are close together
const groupCommentsByProximity = (comments, xAxisMin, xAxisMax, chartWidth = 1000) => {
    if (!comments || comments.length === 0) {
        return [];
    }

    const sortedComments = [...comments].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const timeRange = xAxisMax - xAxisMin;
    const pixelThreshold = 20;
    const timeThreshold = timeRange / chartWidth * pixelThreshold;

    const groupedComments = [];
    let currentGroup = [sortedComments[0]];

    for (let i = 1; i < sortedComments.length; i++) {
        const currentTime = new Date(sortedComments[i].timestamp).getTime();
        const lastGroupTime = new Date(currentGroup[currentGroup.length - 1].timestamp).getTime();

        if (Math.abs(currentTime - lastGroupTime) <= timeThreshold) {
            currentGroup.push(sortedComments[i]);
        } else {
            groupedComments.push({
                comments: currentGroup,
                timestamp: currentGroup[0].timestamp,
                isGroup: currentGroup.length > 1,
                count: currentGroup.length
            });
            currentGroup = [sortedComments[i]];
        }
    }

    if (currentGroup.length > 0) {
        groupedComments.push({
            comments: currentGroup,
            timestamp: currentGroup[0].timestamp,
            isGroup: currentGroup.length > 1,
            count: currentGroup.length
        });
    }

    return groupedComments;
};

const getSeries = (
    selectedKpisData,
    yAxisTitleVisible,
    selectedLimits,
    availableTrendKPIs,
    multiChart,
    energyViewMode = 'overall',
    isOnlyEnergySignalsSelected = false,
    trendComments,
    xAxisMin,
    xAxisMax,
    chartWidth,
    selectedDataPointTimestamp,
    hasComments,
    hasStackedAreaChartVisualization = false
) => {
    const ret = { yAxis: [], seriesData: [], allSeriesLength: 0 };

    // Check if we're in detailed view - only when exactly one signal with StackedAreaChart
    // visualization is selected (hasStackedAreaChartVisualization ensures selectedTrendKPIs.length === 1)
    const isDetailedEnergyView = energyViewMode === energyViewModes.DETAILED &&
        isOnlyEnergySignalsSelected &&
        hasStackedAreaChartVisualization;

    let measuredEnergySignal = null;
    let calculatedEnergySignal = null;
    let mainSignalConfig = null;

    if (isDetailedEnergyView) {
        // Find the main signal (the one with StackedAreaChart visualization)
        const mainSignal = selectedKpisData?.find(item =>
            item?.config?.details?.visualization === STACKED_AREA_CHART
        );

        if (mainSignal) {
            mainSignalConfig = mainSignal?.config;

            // Check if this signal has stacked area chart visualization with subSignals
            if (mainSignalConfig?.details?.visualization === STACKED_AREA_CHART && mainSignalConfig?.details?.subSignals?.length >= 2) {
                // Build a flat lookup of all available KPIs to find sub-signal names reliably
                const flatKpiList = getMergedTrendKpiList(availableTrendKPIs);

                // Find the measured and calculated signals from the selectedKpisData array
                // Use timeseriesKey for identification (standardized, reliable)
                mainSignalConfig.details.subSignals.forEach(subSignal => {
                    const subSignalKey = subSignal?.timeseriesKey;
                    const subSignalData = selectedKpisData?.find(item =>
                        item?.kpiIdentifier === subSignalKey
                    );

                    if (subSignalData) {
                        // Look up the sub-signal in availableTrendKPIs for the correct name
                        // (the name in subSignal metadata from the config list may be incorrect)
                        const kpiFromList = flatKpiList?.find(k => k.timeseriesKey === subSignalKey);
                        const resolvedName = kpiFromList && getTranslatedKPINameV2(kpiFromList)
                            || getTranslatedKPINameV2(subSignal)
                            || subSignal?.timeseriesName
                            || subSignalKey;

                        const enrichedConfig = {
                            ...subSignalData.config,
                            displayName: resolvedName,
                            unit: kpiFromList?.unit || subSignal?.unit || subSignalData.config?.unit,
                            precision: kpiFromList?.timeseriesPrecision || subSignal?.timeseriesPrecision || subSignalData.config?.precision
                        };

                        if (subSignalKey?.toLowerCase().includes(energyConsumptionSignals.MEASURED)) {
                            measuredEnergySignal = {
                                config: enrichedConfig,
                                values: subSignalData?.values || []
                            };
                        } else if (subSignalKey?.toLowerCase().includes(energyConsumptionSignals.CALCULATED)) {
                            calculatedEnergySignal = {
                                config: enrichedConfig,
                                values: subSignalData?.values || []
                            };
                        }
                    }
                });
            }
        }
    }

    // If we have both measured and calculated energy signals in detailed view, create stacked area chart
    if (isDetailedEnergyView && measuredEnergySignal && calculatedEnergySignal && mainSignalConfig) {
        const measuredConfig = measuredEnergySignal?.config || {};
        const calculatedConfig = calculatedEnergySignal?.config || {};
        const unit = mainSignalConfig?.unit || measuredConfig?.unit || '';
        const precision = mainSignalConfig?.timeseriesPrecision || measuredConfig?.timeseriesPrecision || 2;

        const measuredName = measuredConfig?.displayName || getTranslatedKPINameV2(measuredConfig) || '';
        const calculatedName = calculatedConfig?.displayName || getTranslatedKPINameV2(calculatedConfig) || '';

        const measuredValues = getMeasurements(measuredConfig, measuredEnergySignal?.values, false).averages;
        const calculatedValues = getMeasurements(calculatedConfig, calculatedEnergySignal?.values, false).averages;

        // Check if signals are production or consumption based on displayName
        const isEnergyProduction = measuredName.toLowerCase().includes(energyConsumptionSignals.PRODUCTION) ||
            calculatedName.toLowerCase().includes(energyConsumptionSignals.PRODUCTION);

        // Create single y-axis for stacked chart
        ret.yAxis.push({
            labels: {
                format: '{value} ' + unit,
                style: {
                    color: colors.EnergyBlue
                }
            },
            id: 'y_energy_stack',
            title: {
                text: isEnergyProduction ? translate('ABB.Powertrain.Frontend.dailyEnergyProduction') : translate('ABB.Powertrain.Frontend.dailyEnergyConsumption'),
                enabled: yAxisTitleVisible,
                style: {
                    color: '#696969',
                    textAlign: 'center',
                    fontFamily: 'ABBvoice-Regular',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '16px'
                }
            },
            opposite: false,
            visible: true
        });

        // Measured energy consumption series (bottom layer with 60% opacity)
        ret.seriesData.push({
            name: measuredName,
            data: measuredValues,
            type: 'area',
            stacking: 'normal',
            tooltip: {
                valueSuffix: ` ${unit}`,
                valueDecimals: precision
            },
            color: colors.EnergyBlue,
            fillOpacity: 0.6,
            marker: {
                enabled: false
            },
            lineWidth: 2,
            id: 'series_measured_energy',
            yAxis: 'y_energy_stack',
            events: {
                legendItemClick: chartLegendItemClick
            },
        });

        // Calculated energy consumption series (top layer with 60% opacity)
        ret.seriesData.push({
            name: calculatedName,
            data: calculatedValues,
            type: 'area',
            stacking: 'normal',
            tooltip: {
                valueSuffix: ` ${unit}`,
                valueDecimals: precision
            },
            color: colors.EnergyBlueTransparent,
            fillOpacity: 0.6,
            marker: {
                enabled: false
            },
            lineWidth: 2,
            id: 'series_calculated_energy',
            yAxis: 'y_energy_stack',
            events: {
                legendItemClick: chartLegendItemClick
            },
        });

        ret.allSeriesLength = 2;

        // Add comment strip in detailed view (same as overall view)
        const hasChartData = selectedKpisData?.some(item => item?.values?.length > 0);
        if (hasComments && multiChart !== multiChartOptions.MULTI && hasChartData) {
            // Resize stacked area y-axis to leave room for the comment strip at bottom
            ret.yAxis[0].top = '0%';
            ret.yAxis[0].height = '80%';

            ret.yAxis.push({
                id: COMMENT_YAXIS_ID,
                title: { enabled: false },
                labels: { enabled: false },
                top: '85%',
                height: '12%',
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
                visible: true,
                min: 0,
                max: 1,
                plotLines: [{
                    color: 'rgba(31, 31, 31, 1)',
                    width: 1.5,
                    value: 1,
                    zIndex: 1
                }, {
                    color: 'transparent',
                    width: 35,
                    value: 0.5,
                    className: 'trend-comment-plotline',
                    zIndex: 1,
                    ...trendComments?.length === 0 ? {
                        label: {
                            text: `<span class="comment-area-clickable" style="cursor: pointer;">${translate('ABB.Powertrain.Frontend.noCommentsInSelectedTimePeriod')}</span>`,
                            align: 'center',
                            verticalAlign: 'middle',
                            y: 0,
                            style: {
                                color: 'rgba(105, 105, 105, 1)',
                                fontSize: '14px',
                                fontWeight: '400',
                            },
                            useHTML: true,
                        }
                    } : {}
                }],
            });

            if (trendComments?.length) {
                const groupedComments = groupCommentsByProximity(trendComments, xAxisMin, xAxisMax, chartWidth);
                const selectedTimestamp = selectedDataPointTimestamp?.split(' | ')?.[0]?.trim();
                ret.seriesData.push({
                    id: 'comment-series',
                    data: groupedComments?.map((commentGroup) => ({
                        x: new Date(commentGroup.timestamp).getTime(),
                        y: 0.5,
                        comment: commentGroup.comments[0],
                        commentGroup: commentGroup.isGroup ? commentGroup.comments : null,
                        isGroup: commentGroup.isGroup,
                        count: commentGroup.count,
                        marker: { enabled: false },
                        dataLabels: {
                            useHTML: true,
                            enabled: false,
                            formatter: function () {
                                return renderToStaticMarkup(
                                    <div className={`graph-comment-icon${new Date(selectedTimestamp).getTime() === this.point.x ? ' selected' : ''}`}>
                                        <IconTrendComment />
                                        {this.point.isGroup &&
                                            <span className='comment-badge primary-badge'>
                                                {this.point.count}
                                            </span>
                                        }
                                    </div>
                                );
                            }
                        }
                    })) || [],
                    yAxis: COMMENT_YAXIS_ID,
                    showInLegend: false,
                    enableMouseTracking: true,
                    visible: true,
                    marker: { enabled: false },
                });
            }
        }

        return ret;
    }

    // Regular processing for non-detailed energy view or regular signals
    // Filter out sub-signals if we're in overall view with a stacked area chart
    const mainStackedSignal = selectedKpisData?.find(item =>
        item?.config?.details?.visualization === STACKED_AREA_CHART
    );

    const dataToProcess = mainStackedSignal && energyViewMode === energyViewModes.OVERALL
        ? selectedKpisData.filter(item => {
            // In Overall view, only include the main signal, not the sub-signals
            if (item?.config?.details?.visualization === STACKED_AREA_CHART) {
                return true;
            }
            // Exclude if this is a sub-signal of the stacked area chart
            const isSubSignal = mainStackedSignal?.config?.details?.subSignals?.some(
                subSig => subSig.timeseriesKey === item?.kpiIdentifier
            );
            return !isSubSignal;
        })
        : selectedKpisData;

    transform(dataToProcess, (acc, item, index) => {
        const config = item.config ? item.config : {};
        if (isEmpty(config)) {
            return { yAxis: [], seriesData: [], allSeriesLength: 0 };
        }
        const unit = config && config.unit || '';
        const precision = config && config.precision;
        const name = getTranslatedKPINameV2(config);
        const trendDataValues = item.values;
        const trendKPIs = getMergedTrendKpiList(availableTrendKPIs);
        const seriesId = `series_${getSeriesIndexFromList(config, trendKPIs)}`;
        const yAxisId = `y_${index}`;
        const { averages, rangedata } = getMeasurements(config, trendDataValues, selectedKpisData?.length === 1);
        acc.yAxis.push({
            labels: {
                format: '{value} ' + unit,
                style: {
                    color: item.color
                }
            },
            id: yAxisId,
            title: {
                text: name,
                enabled: yAxisTitleVisible,
                style: {
                    color: item.color
                }
            },
            opposite: index % 2 !== 0,
            visible: true,
            top: '0%',
            height: hasComments && multiChart !== multiChartOptions.MULTI ? '80%' : '100%'
        });

        // Regular line series
        acc.seriesData.push({
            name: `${name}`,
            data: averages,
            tooltip: {
                valueSuffix: ` ${unit}`,
                valueDecimals: precision
            },
            color: item.color,
            marker: {
                enabled: true,
                radius: 2
            },
            lineWidth: 1,
            id: seriesId,
            yAxis: yAxisId,
            events: {
                legendItemClick: chartLegendItemClick
            },
        });
        if (rangedata.length) {
            acc.seriesData.push({
                name: `${name}`,
                data: rangedata,
                type: 'arearange',
                id: 'rangeSeries',
                lineWidth: 2,
                radius: 2,
                linkedTo: ':previous',
                fillOpacity: 0.3,
                opacity: 0.3,
                color: item.color,
                zIndex: 0,
                yAxis: yAxisId,
                tooltip: {
                    valueSuffix: ` ${unit}`,
                },
                marker: {
                    enabled: true,
                    radius: 2
                }
            });
        }

        // PLOT LINES ALL DEFAULT+CUSTOM LIMITS IN MULTI TRENDS CHART
        if (multiChart === multiChartOptions.MULTI && config?.thresholdList?.length) {
            config?.thresholdList?.map((sl) => {
                if (!sl?.isUserLimit) {
                    const limtsDataSeries = generateMarkerData(averages, +sl?.limitValue);
                    if (limtsDataSeries?.length) {
                        const seriesWithLimits = addLimitsInSeries(sl, limtsDataSeries, unit, yAxisId);
                        acc?.seriesData?.push(seriesWithLimits);
                    }
                }
            });
        }
        // PLOT LINES FOR SELECTED LIMITS IN SINGLE TRENDS CHART
        if (multiChart !== multiChartOptions.MULTI && selectedKpisData?.length === 1) {
            if (selectedLimits?.length) {
                selectedLimits.map((sl) => {
                    const limtsDataSeries = generateMarkerData(averages, +sl?.limitValue);
                    if (limtsDataSeries?.length) {
                        const seriesWithLimits = addLimitsInSeries(sl, limtsDataSeries, unit, yAxisId);
                        acc?.seriesData?.push(seriesWithLimits);
                    }
                });
            }
        }
        acc?.seriesData?.sort((a, b) => LIMIT_LINES_ORDER.indexOf(a.name) - LIMIT_LINES_ORDER.indexOf(b.name));
        acc?.seriesData?.sort((a, b) => a.name === b.name && b.limitValue - a.limitValue); // SORT LIMIT VALUES IN DESC ORDER IF SAME LIMIT TYPE AND DIRECTION
    }, ret);

    // Only add comment series if there's data in the chart
    const hasChartData = selectedKpisData?.some(item => item?.values?.length > 0);
    if (hasComments && multiChart !== multiChartOptions.MULTI && hasChartData) {

        ret.yAxis.push({
            id: COMMENT_YAXIS_ID,
            title: {
                enabled: false
            },
            labels: {
                enabled: false
            },
            top: '85%',
            height: '12%',
            gridLineWidth: 0,
            lineWidth: 0,
            tickLength: 0,
            visible: true,
            min: 0,
            max: 1,
            plotLines: [{
                color: 'rgba(31, 31, 31, 1)',
                width: 1.5,
                value: 1,
                zIndex: 1
            }, {
                color: 'transparent',
                width: 35,
                value: 0.5,
                className: 'trend-comment-plotline',
                zIndex: 1,
                ...trendComments?.length === 0 ? {
                    label: {
                        text: `<span class="comment-area-clickable" style="cursor: pointer;">${translate('ABB.Powertrain.Frontend.noCommentsInSelectedTimePeriod')}</span>`,
                        align: 'center',
                        verticalAlign: 'middle',
                        y: 0,
                        style: {
                            color: 'rgba(105, 105, 105, 1)',
                            fontSize: '14px',
                            fontWeight: '400',
                        },
                        useHTML: true,
                    }
                } : {}
            }],
        });

        const getCommentDataPoints = () => {
            // Group comments by proximity
            const groupedComments = groupCommentsByProximity(trendComments, xAxisMin, xAxisMax, chartWidth);
            const selectedTimestamp = selectedDataPointTimestamp?.split(' | ')?.[0]?.trim();

            return groupedComments?.map((commentGroup) => ({
                x: new Date(commentGroup.timestamp).getTime(),
                y: 0.5,
                comment: commentGroup.comments[0],
                commentGroup: commentGroup.isGroup ? commentGroup.comments : null,
                isGroup: commentGroup.isGroup,
                count: commentGroup.count,
                marker: {
                    enabled: false,
                },
                dataLabels: {
                    useHTML: true,
                    enabled: false,
                    formatter: function () {
                        return renderToStaticMarkup(
                            <div className={`graph-comment-icon${new Date(selectedTimestamp).getTime() === this.point.x ? ' selected' : ''}`}>
                                <IconTrendComment />
                                {this.point.isGroup &&
                                    <span className='comment-badge primary-badge'>
                                        {this.point.count}
                                    </span>
                                }
                            </div>
                        );
                    }
                }
            })) || [];
        };

        if (trendComments?.length) {
            ret.seriesData.push({
                id: 'comment-series',
                data: getCommentDataPoints(),
                yAxis: COMMENT_YAXIS_ID,
                showInLegend: false,
                enableMouseTracking: true,
                visible: true,
                marker: {
                    enabled: false,
                },
            });
        }
    }

    const allSeriesLength = ret.seriesData?.length ? ret.seriesData.reduce((count, series) => series.data?.length + count, 0) : 0;

    return { ...ret, allSeriesLength };
};

const useChartConfig = (
    selectedKpisData,
    userLimits,
    yAxisTitleVisible = true,
    removeKPIData,
    multiChart,
    selectedLimits,
    availableTrendKPIs,
    energyViewMode,
    isOnlyEnergySignalsSelected,
    trendComments,
    setShowCommentDrawer,
    setShowCommentModal,
    chartInstance,
    selectedDataPointTimestamp,
    hasStackedAreaChartVisualization = false
) => {
    const [options, setOptions] = useState({});
    const chartConfig = getChartConfig(removeKPIData);
    const userCapabilityHelper = useUserCapability();
    const hasComments = userCapabilityHelper.hasOperationalParametersComments();

    useEffect(() => {
        // Check if we're in detailed energy view - only when exactly one signal with
        // StackedAreaChart visualization is selected
        const isDetailedEnergyView = energyViewMode === energyViewModes.DETAILED &&
            isOnlyEnergySignalsSelected &&
            hasStackedAreaChartVisualization;

        const hasChartData = selectedKpisData?.some(item => item?.values?.length > 0);

        // Get x-axis min/max from chart instance if available, otherwise use default
        const xAxisMin = chartInstance?.xAxis?.[0]?.min;
        const xAxisMax = chartInstance?.xAxis?.[0]?.max;
        const chartWidth = chartInstance?.plotWidth || 1000;

        const { yAxis, seriesData, allSeriesLength } = getSeries(
            selectedKpisData,
            yAxisTitleVisible,
            selectedLimits,
            availableTrendKPIs,
            multiChart,
            energyViewMode,
            isOnlyEnergySignalsSelected,
            trendComments,
            xAxisMin,
            xAxisMax,
            chartWidth,
            selectedDataPointTimestamp,
            hasComments,
            hasStackedAreaChartVisualization
        );

        if (yAxis.length === 0 && seriesData.length === 0) {
            return;
        }

        chartConfig.yAxis = yAxis;

        // Preserve visibility for matching ids even if helper series (e.g. comments) change the count
        const visibilityById = Array.isArray(chartInstance?.series)
            ? chartInstance.series.reduce((acc, s) => {
                const id = s?.options?.id;
                if (id) { acc[id] = s.visible; }
                return acc;
            }, {})
            : null;

        chartConfig.series = visibilityById
            ? seriesData.map((s) => Object.hasOwn(visibilityById, s.id)
                ? { ...s, visible: visibilityById[s.id] }
                : s)
            : seriesData;
        chartConfig.plotOptions.series.boostThreshold = allSeriesLength > defaultBoostThreshold ? 1 : 0;
        chartConfig.lang.noData = renderToStaticMarkup(
            <ChartNoDataText
                title={translate('ABB.Powertrain.Frontend.noDataLabelAvailable')}
                text={translate('ABB.Powertrain.Frontend.adjustDateRangeOrChartType')} />);
        chartConfig.enableNoDataZoom = true;
        chartConfig.tooltip.formatter = function () {
            const filteredPoints = this.points?.filter(point => point.series.options.id !== 'comment-series');
            if (!filteredPoints || filteredPoints.length === 0) {
                return false;
            }
            return renderToStaticMarkup(<TrendTooltip points={filteredPoints} timeStamp={this.x} />);
        };
        chartConfig.legend.labelFormatter = function () {
            return renderToStaticMarkup(<RemovableLegend
                signalSourceName={this.userOptions.component}
                legend={this}
                multiChart={isDetailedEnergyView ? multiChartOptions.MULTI : multiChart}
                symbolType={this.symbol}
            />);
        };

        // Function to attach plotlines comment click handlers
        const attachClickHandlers = (chart) => {
            const plotLineElement = chart?.container?.querySelector('.trend-comment-plotline');
            const labelElement = chart?.container?.querySelector('.comment-area-clickable');
            const iconElements = chart?.container?.querySelectorAll('.graph-comment-icon');

            // Create tooltip container if it doesn't exist
            let tooltipContainer = chart?.container?.querySelector('.custom-comment-tooltip');
            if (!tooltipContainer) {
                tooltipContainer = document.createElement('div');
                tooltipContainer.className = 'custom-comment-tooltip';
                tooltipContainer.style.cssText = 'position: absolute; display: none; z-index: 9999; pointer-events: none;';
                chart?.container?.appendChild(tooltipContainer);
            }

            // cursor icon for comment area
            let cursorIcon = chart?.container?.querySelector('.cursor-comment-icon');
            if (!cursorIcon) {
                cursorIcon = document.createElement('div');
                cursorIcon.className = 'cursor-comment-icon';
                cursorIcon.innerHTML = renderToStaticMarkup(<IconTrendComment />);
                cursorIcon.style.cssText = 'position: absolute; display: none; z-index: 100; width: 16px; height: 16px; pointer-events: none; left: 0; top: 0;';
                chart?.container?.appendChild(cursorIcon);
            }

            const getCommentBounds = () => {
                const left = chart.plotLeft;
                const right = chart.plotLeft + chart.plotWidth;
                const commentAxis = chart?.yAxis?.find((axis) => axis?.options?.id === COMMENT_YAXIS_ID);
                if (!commentAxis) {
                    return {
                        left,
                        right,
                        top: chart.plotTop,
                        bottom: chart.plotTop + chart.plotHeight
                    };
                }
                const topPixel = commentAxis.toPixels(1);
                const bottomPixel = commentAxis.toPixels(0);
                const top = Math.min(topPixel, bottomPixel) - 2;
                const bottom = Math.max(topPixel, bottomPixel);
                return { left, right, top, bottom };
            };

            const updateCursorPosition = (normalizedEvent) => {
                if (!cursorIcon) {
                    return;
                }
                const { left, right, top, bottom } = getCommentBounds();
                const iconSize = 16;
                const half = iconSize / 2;
                const clampedX = Math.min(Math.max(normalizedEvent.chartX, left + half), right - half) - half + 15;
                const clampedY = Math.min(Math.max(normalizedEvent.chartY, top + half), bottom - half) - half;
                cursorIcon.style.display = 'block';
                cursorIcon.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
            };

            if (chart?.container) {
                if (chart.container.__commentHoverHandler) {
                    chart.container.removeEventListener('mousemove', chart.container.__commentHoverHandler);
                }
                if (chart.container.__commentHoverLeaveHandler) {
                    chart.container.removeEventListener('mouseleave', chart.container.__commentHoverLeaveHandler);
                }

                chart.container.__commentHoverHandler = (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    const { left, right, top, bottom } = getCommentBounds();
                    const target = e.target;
                    const isOverCommentIcon = target?.closest('.highcharts-comment-icon') || target?.closest('.graph-comment-icon');

                    if (
                        normalizedEvent.chartX >= left &&
                        normalizedEvent.chartX <= right &&
                        normalizedEvent.chartY >= top &&
                        normalizedEvent.chartY <= bottom &&
                        !isOverCommentIcon
                    ) {
                        updateCursorPosition(normalizedEvent);
                    } else if (cursorIcon) {
                        cursorIcon.style.display = 'none';
                    }
                };
                chart.container.__commentHoverLeaveHandler = () => {
                    if (cursorIcon) {
                        cursorIcon.style.display = 'none';
                    }
                };
                chart.container.addEventListener('mousemove', chart.container.__commentHoverHandler);
                chart.container.addEventListener('mouseleave', chart.container.__commentHoverLeaveHandler);
            }

            if (iconElements) {
                iconElements?.forEach((iconElement, index) => {
                    const scatterSeries = chart?.series?.find((s) => s.options.id === 'comment-series');
                    const point = scatterSeries?.options?.data?.[index];

                    if (iconElement && point) {
                        const newIcon = iconElement.cloneNode(true);
                        iconElement.parentNode.replaceChild(newIcon, iconElement);
                        newIcon.style.cursor = 'pointer';

                        // Hide cursor icon when hovering over existing comment icon
                        newIcon.addEventListener('mouseenter', () => {
                            if (cursorIcon) {
                                cursorIcon.style.display = 'none';
                            }
                            let tooltipContent;
                            if (point?.isGroup) {
                                tooltipContent = renderToStaticMarkup(
                                    <TrendGroupCommentsTooltip point={point} />
                                );
                            } else {
                                tooltipContent = renderToStaticMarkup(
                                    <TrendCommentsTooltip comment={point.comment} />
                                );
                            }

                            tooltipContainer.innerHTML = tooltipContent;
                            tooltipContainer.style.display = 'block';

                            // Position tooltip near the icon
                            const rect = newIcon.getBoundingClientRect();
                            const containerRect = chart?.container?.getBoundingClientRect();
                            const tooltipRect = tooltipContainer.getBoundingClientRect();

                            const horizontalGap = 10; // Gap between icon and tooltip
                            const padding = 5; // Padding from container edges

                            // Calculate available space
                            const spaceRight = containerRect.right - rect.right;
                            const spaceLeft = rect.left - containerRect.left;
                            const spaceBelow = containerRect.bottom - rect.bottom;
                            const spaceAbove = rect.top - containerRect.top;

                            let left, top;

                            // Determine horizontal position (prefer right side)
                            if (spaceRight >= tooltipRect.width + horizontalGap) {
                                // Position to the right of icon
                                left = rect.right - containerRect.left + horizontalGap;
                            } else if (spaceLeft >= tooltipRect.width + horizontalGap) {
                                // Position to the left of icon
                                left = rect.left - containerRect.left - tooltipRect.width - horizontalGap;
                            } else {
                                // Not enough space on either side, center align below icon
                                left = rect.left - containerRect.left + rect.width / 2 - tooltipRect.width / 2;
                                // Adjust if overflow
                                if (left < padding) {
                                    left = padding;
                                } else if (left + tooltipRect.width > containerRect.width - padding) {
                                    left = containerRect.width - tooltipRect.width - padding;
                                }
                            }

                            // Determine vertical position (prefer below)
                            if (spaceBelow >= tooltipRect.height + 10) {
                                // Position below the icon
                                top = rect.bottom - containerRect.top + 5;
                            } else if (spaceAbove >= tooltipRect.height + 10) {
                                // Position above the icon
                                top = rect.top - containerRect.top - tooltipRect.height - 5;
                            } else {
                                // Center vertically with icon
                                top = rect.top - containerRect.top + rect.height / 2 - tooltipRect.height / 2;
                            }

                            // Ensure tooltip stays within container bounds
                            if (top < padding) {
                                top = padding;
                            }
                            if (top + tooltipRect.height > containerRect.height - padding) {
                                top = containerRect.height - tooltipRect.height - padding;
                            }

                            tooltipContainer.style.left = `${left}px`;
                            tooltipContainer.style.top = `${top}px`;
                        });

                        newIcon.addEventListener('mouseleave', () => {
                            tooltipContainer.style.display = 'none';
                        });

                        newIcon.addEventListener('click', (e) => {
                            e.stopPropagation();
                            tooltipContainer.style.display = 'none';
                            if (point?.isGroup) {
                                setShowCommentDrawer(true, point.commentGroup);
                            } else {
                                setShowCommentDrawer(true, point.comment);
                            }
                        });
                    }
                });
            }

            if (plotLineElement) {
                const newPlotLine = plotLineElement.cloneNode(true);
                plotLineElement.parentNode.replaceChild(newPlotLine, plotLineElement);

                newPlotLine.style.cursor = 'pointer';

                // mouse move for cursor icon
                newPlotLine.addEventListener('mouseenter', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    updateCursorPosition(normalizedEvent);
                });

                newPlotLine.addEventListener('mousemove', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    updateCursorPosition(normalizedEvent);
                });

                newPlotLine.addEventListener('mouseleave', () => {
                    if (cursorIcon) {
                        cursorIcon.style.display = 'none';
                    }
                });

                newPlotLine.addEventListener('click', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    const timestamp = chart.xAxis[0].toValue(normalizedEvent.chartX);
                    const utcTime = new Date(timestamp).toISOString();
                    setShowCommentModal(true, false, utcTime);
                });
            }

            if (labelElement) {
                const newLabel = labelElement.cloneNode(true);
                labelElement.parentNode.replaceChild(newLabel, labelElement);

                // mouse move for cursor icon on label
                newLabel.addEventListener('mouseenter', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    updateCursorPosition(normalizedEvent);
                });

                newLabel.addEventListener('mousemove', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    updateCursorPosition(normalizedEvent);
                });

                newLabel.addEventListener('mouseleave', () => {
                    if (cursorIcon) {
                        cursorIcon.style.display = 'none';
                    }
                });

                newLabel.addEventListener('click', (e) => {
                    const normalizedEvent = chart.pointer.normalize(e);
                    const timestamp = chart.xAxis[0].toValue(normalizedEvent.chartX);
                    const utcTime = new Date(timestamp).toISOString();
                    setShowCommentModal(true, false, utcTime);
                });
            }
        };

        if (hasComments && multiChart !== multiChartOptions.MULTI) {
            if (hasChartData) {
                chartConfig.chart.events = {
                    ...chartConfig.chart.events,
                    load: function () {
                        const chart = this;
                        setTimeout(() => {
                            attachClickHandlers(chart);
                        }, 100);
                    },
                    redraw: function () {
                        const chart = this;
                        setTimeout(() => {
                            attachClickHandlers(chart);
                        }, 100);
                    }
                };
            }
        }

        setOptions({ ...chartConfig });
    }, [selectedKpisData, userLimits, trendComments, chartInstance]);

    return options;
};
// Exported for testing purposes
export {
    getChartConfig,
    getMeasurements,
    generateMarkerData,
    getSeriesIndexFromList,
    addLimitsInSeries,
    getSeries
};

export default useChartConfig;
